import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface DashboardMetrics {
  totalProducts: number;
  totalValue: number;
  lowStockCount: number;
  totalSales: number;
  monthlyRevenue: number;
  todayRevenue: number;
  totalWithdrawals: number;
  averageOrderValue: number;
}

interface DashboardAnalysis {
  insights: string;
  suggestions: string[];
  analyzedAt: string;
}

const STORAGE_KEY = 'dashboard_analysis';
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

const shouldAnalyze = (preferredHour: number = 9): boolean => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const now = new Date();
    const currentHour = now.getHours();
    
    // Si pas d'analyse précédente, vérifier si on est à l'heure préférée
    if (!stored) {
      return currentHour >= preferredHour;
    }
    
    const data = JSON.parse(stored);
    const analyzedAt = new Date(data.analyzedAt);
    const timeSinceLastAnalysis = now.getTime() - analyzedAt.getTime();
    
    // Vérifier que 24h se sont écoulées
    if (timeSinceLastAnalysis < ONE_DAY_MS) {
      return false;
    }
    
    // Vérifier que l'heure actuelle est >= à l'heure préférée
    // et qu'on n'a pas déjà fait l'analyse aujourd'hui
    const isSameDay = analyzedAt.toDateString() === now.toDateString();
    return !isSameDay && currentHour >= preferredHour;
  } catch {
    return true;
  }
};

const getStoredAnalysis = (): DashboardAnalysis | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const saveAnalysis = (analysis: DashboardAnalysis) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(analysis));
  } catch (error) {
    console.error('Error saving analysis:', error);
  }
};

export const useDashboardAnalysis = (
  metrics: DashboardMetrics | null, 
  preferredHour: number = 9,
  managementMode: string | null = null
) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<DashboardAnalysis | null>(null);
  const [lastCheckTime, setLastCheckTime] = useState<number>(Date.now());

  useEffect(() => {
    const initAnalysis = async () => {
      // Charger l'analyse existante
      const stored = getStoredAnalysis();
      if (stored) {
        setAnalysis(stored);
      }

      // Vérifier si une nouvelle analyse est nécessaire
      if (metrics && shouldAnalyze(preferredHour)) {
        await performAnalysis();
      }
    };

    initAnalysis();

    // Vérifier toutes les heures si on doit lancer l'analyse
    const interval = setInterval(() => {
      const now = Date.now();
      // Ne vérifier que si plus d'une heure s'est écoulée
      if (now - lastCheckTime >= 60 * 60 * 1000) {
        setLastCheckTime(now);
        if (metrics && shouldAnalyze(preferredHour)) {
          performAnalysis();
        }
      }
    }, 60 * 60 * 1000); // Vérifier toutes les heures

    return () => clearInterval(interval);
  }, [metrics, preferredHour, lastCheckTime]);

  const performAnalysis = async () => {
    if (!metrics || isAnalyzing) return;

    // Vérification stricte: ne pas analyser si moins de 24h depuis la dernière
    const stored = getStoredAnalysis();
    if (stored) {
      const lastAnalysis = new Date(stored.analyzedAt).getTime();
      const now = Date.now();
      if (now - lastAnalysis < ONE_DAY_MS) {
        toast.error('Une analyse a déjà été effectuée dans les dernières 24h');
        return;
      }
    }

    setIsAnalyzing(true);
    try {
      const { data, error } = await supabase.functions.invoke('analyze-dashboard', {
        body: { 
          metrics,
          managementMode 
        },
      });

      if (error) throw error;

      if (data?.insights && data?.suggestions) {
        const newAnalysis: DashboardAnalysis = {
          insights: data.insights,
          suggestions: data.suggestions,
          analyzedAt: new Date().toISOString(),
        };
        
        setAnalysis(newAnalysis);
        saveAnalysis(newAnalysis);
        toast.success('Analyse du dashboard générée');
      }
    } catch (error) {
      console.error('Error analyzing dashboard:', error);
      toast.error('Erreur lors de l\'analyse du dashboard');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const refreshAnalysis = () => {
    performAnalysis();
  };

  return {
    isAnalyzing,
    analysis,
    refreshAnalysis,
  };
};
